import type { Localized } from './home.content';
import type { DemoKey } from './solutions.content';

/**
 * Landing-page hero, platform showcase and "Built with you" copy.
 *
 * Claims and their sources:
 *   - "600+ features shipped in the last 3 months": figure supplied by the
 *     founders (2026-09-27) from their feature-request tracking. For
 *     reference, development shows 462 `feat(...)` commits and 425 merged PRs
 *     between 2026-06-27 and 2026-09-27. Re-check before reusing elsewhere.
 *   - Weekly releases: hundreds of merged PRs per quarter.
 * Update deliberately, never automatically.
 */

export const HOME_HERO = {
  label: {
    en: 'Bookings · Payments · WhatsApp AI · CRM',
    ar: 'الحجوزات · المدفوعات · واتساب الذكي · إدارة العملاء',
  },
  title: {
    en: 'Booking and membership operating system.',
    ar: 'نظام تشغيل الحجوزات والعضويات.',
  },
  titleMuted: {
    en: 'Shaped around the way you work.',
    ar: 'مصمم حول طريقة عملك.',
  },
  description: {
    en: 'Your own branded booking app, a WhatsApp AI concierge, payments, memberships and CRM. We set it up for your business, then keep building what you ask for.',
    ar: 'تطبيق حجز باسم منشأتك، ومساعد ذكي على واتساب، ومدفوعات وعضويات وإدارة عملاء. نهيّئ المنصة لعملك، ثم نواصل بناء ما تطلبه.',
  },
  primaryCta: { en: 'Schedule a demo', ar: 'احجز عرضًا توضيحيًا' },
  secondaryCta: { en: 'See it in action', ar: 'شاهدها وهي تعمل' },
  audienceLead: { en: 'Built for', ar: 'مصممة لـ' },
  modes: {
    label: { en: 'What customers book', ar: 'ما يحجزه العملاء' },
    courts: { en: 'Courts', ar: 'الملاعب' },
    classes: { en: 'Classes', ar: 'الحصص' },
    appointments: { en: 'Appointments', ar: 'المواعيد' },
  },
} as const;

export const HERO_EVENTS = [
  {
    kind: 'whatsapp',
    title: { en: 'Booked over WhatsApp', ar: 'حجز عبر واتساب' },
    detail: { en: 'Court 3 · Thu 8:00 PM', ar: 'الملعب 3 · الخميس 8:00 م' },
  },
  {
    kind: 'class',
    title: { en: 'Class booked', ar: 'تم حجز حصة' },
    detail: { en: 'Reformer Flow · 7:00 AM', ar: 'ريفورمر فلو · 7:00 ص' },
  },
  {
    kind: 'class',
    title: { en: 'Appointment booked', ar: 'تم حجز موعد' },
    detail: { en: 'Physio with Lina · 4:30 PM', ar: 'علاج طبيعي مع لينا · 4:30 م' },
  },
  {
    kind: 'payment',
    title: { en: 'Payment received', ar: 'تم استلام الدفعة' },
    detail: { en: 'Split 4 ways · fully paid', ar: 'مقسّمة على 4 · مدفوعة بالكامل' },
  },
  {
    kind: 'invoice',
    title: { en: 'E-invoice issued', ar: 'صدرت الفاتورة الإلكترونية' },
    detail: { en: 'Synced to your accounting', ar: 'تمت مزامنتها مع نظامك المحاسبي' },
  },
  {
    kind: 'member',
    title: { en: 'New member joined', ar: 'انضم عضو جديد' },
    detail: { en: 'Family membership', ar: 'عضوية عائلية' },
  },
] as const;

export interface PlatformTab {
  id: DemoKey;
  label: Localized;
  heading: Localized;
  description: Localized;
  points: Localized[];
}

export const PLATFORM_SECTION = {
  label: { en: 'The platform', ar: 'المنصة' },
  heading: {
    en: 'Everything your customers and your team touch, in one system',
    ar: 'كل ما يستخدمه عملاؤك وفريقك، في نظام واحد',
  },
  description: {
    en: 'These are the real flows your members and staff use every day, recreated with sample data.',
    ar: 'هذه هي المسارات الفعلية التي يستخدمها أعضاؤك وفريقك كل يوم، أعدنا بناءها ببيانات تجريبية.',
  },
  pause: { en: 'Pause the tour', ar: 'إيقاف الجولة' },
  play: { en: 'Play the tour', ar: 'تشغيل الجولة' },
} as const;

export const PLATFORM_TABS: PlatformTab[] = [
  {
    id: 'branded-app',
    label: { en: 'Member app', ar: 'تطبيق الأعضاء' },
    heading: {
      en: 'An app with your name on it',
      ar: 'تطبيق يحمل اسمك',
    },
    description: {
      en: 'Members book, pay and manage everything from your own app and website, never from a shared marketplace.',
      ar: 'يحجز أعضاؤك ويدفعون ويديرون كل شيء من تطبيقك وموقعك الخاص، لا من سوق مشترك.',
    },
    points: [
      { en: 'Branded iOS and Android app and booking website', ar: 'تطبيق iOS وAndroid وموقع حجز بعلامتك' },
      { en: 'Book and pay in a few taps', ar: 'الحجز والدفع بلمسات قليلة' },
      { en: 'Arabic and English, with full right-to-left', ar: 'بالعربية والإنجليزية مع دعم كامل للاتجاه من اليمين' },
    ],
  },
  {
    id: 'whatsapp',
    label: { en: 'WhatsApp AI', ar: 'واتساب الذكي' },
    heading: {
      en: 'A concierge that answers at 2 AM',
      ar: 'مساعد يرد حتى الثانية فجرًا',
    },
    description: {
      en: 'Customers ask on WhatsApp. Flow checks live availability, books the slot and sends a payment link inside the chat.',
      ar: 'يسأل العملاء عبر واتساب، فتتحقق Flow من التوفر الفعلي وتحجز الموعد وترسل رابط الدفع داخل المحادثة.',
    },
    points: [
      { en: 'Official WhatsApp Business API', ar: 'واجهة واتساب للأعمال الرسمية' },
      { en: 'Confirmations and reminders sent for you', ar: 'التأكيدات والتذكيرات تُرسل تلقائيًا' },
      { en: 'Hands the chat to your team when needed', ar: 'يحوّل المحادثة لفريقك عند الحاجة' },
    ],
  },
  {
    id: 'calendar',
    label: { en: 'Operations', ar: 'التشغيل' },
    heading: {
      en: 'Every court, class and coach on one calendar',
      ar: 'كل ملعب وحصة ومدرب في تقويم واحد',
    },
    description: {
      en: 'Your front desk sees the whole day at a glance, moves bookings with a drag, and customers are told about every change.',
      ar: 'يرى فريق الاستقبال اليوم كاملًا بنظرة واحدة، وينقل الحجوزات بالسحب، ويصل العملاء إشعار بكل تغيير.',
    },
    points: [
      { en: 'Drag to move, extend or block', ar: 'اسحب للنقل أو التمديد أو الحظر' },
      { en: 'Many locations, roles scoped per site', ar: 'مواقع متعددة وصلاحيات لكل فرع' },
      { en: 'Coaches get their own schedules', ar: 'لكل مدرب جدوله الخاص' },
    ],
  },
  {
    id: 'payments',
    label: { en: 'Payments', ar: 'المدفوعات' },
    heading: {
      en: 'Collected, split and invoiced, automatically',
      ar: 'تحصيل وتقسيم وفوترة، تلقائيًا',
    },
    description: {
      en: 'Every booking, package and membership is paid online and turned into a tax-compliant e-invoice without anyone touching a spreadsheet.',
      ar: 'كل حجز وباقة وعضوية تُدفع إلكترونيًا وتتحول إلى فاتورة إلكترونية متوافقة ضريبيًا دون أن يلمس أحد جدول بيانات.',
    },
    points: [
      { en: 'Cards, Apple Pay and local methods via licensed gateways', ar: 'البطاقات وApple Pay والوسائل المحلية عبر بوابات مرخصة' },
      { en: 'Players split the cost between them', ar: 'يقسم اللاعبون التكلفة بينهم' },
      { en: 'E-invoices synced to Oracle, Odoo, Zoho Books, Wafeq and more', ar: 'فواتير إلكترونية تتزامن مع Oracle وOdoo وZoho Books وWafeq وغيرها' },
    ],
  },
  {
    id: 'crm',
    label: { en: 'CRM & insights', ar: 'العملاء والتحليلات' },
    heading: {
      en: 'Know every member, and who is drifting away',
      ar: 'اعرف كل عضو، ومن بدأ يبتعد',
    },
    description: {
      en: 'Full history for every customer, and an AI assistant you can ask about your business in plain language.',
      ar: 'سجل كامل لكل عميل، ومساعد ذكي تسأله عن أعمالك بلغة بسيطة.',
    },
    points: [
      { en: 'Bookings, packages and preferences per customer', ar: 'الحجوزات والباقات والتفضيلات لكل عميل' },
      { en: 'Ask questions, get answers from your own data', ar: 'اسأل واحصل على إجابات من بياناتك' },
      { en: 'Win-back campaigns on WhatsApp', ar: 'حملات استعادة العملاء عبر واتساب' },
    ],
  },
];

export const BUILT_WITH_YOU = {
  label: { en: 'Built with you', ar: 'نبنيها معك' },
  heading: {
    en: 'Ask for it. We build it.',
    ar: 'اطلبها، ونحن نبنيها.',
  },
  description: {
    en: 'Most booking software asks you to change how you run your business. We work the other way around. Your requests go straight to the team that builds Flow, and many of the features clubs use every day started as one venue’s idea.',
    ar: 'معظم أنظمة الحجز تطلب منك تغيير طريقة إدارة عملك، ونحن نعمل بالعكس. تصل طلباتك مباشرة إلى الفريق الذي يبني Flow، وكثير من المزايا التي تستخدمها الأندية يوميًا بدأت فكرةً من منشأة واحدة.',
  },
  stats: [
    {
      value: { en: '600+', ar: '600+' },
      label: { en: 'Features shipped in the last 3 months', ar: 'ميزة أُطلقت خلال آخر 3 أشهر' },
    },
    {
      value: { en: 'Weekly', ar: 'أسبوعيًا' },
      label: { en: 'New releases', ar: 'إصدارات جديدة' },
    },
    {
      value: { en: '1:1', ar: '1:1' },
      label: { en: 'A direct line to the builders', ar: 'تواصل مباشر مع من يبنون المنصة' },
    },
  ],
  demoCaption: {
    en: 'Recent features that started as client requests',
    ar: 'مزايا حديثة بدأت بطلبات من عملائنا',
  },
  steps: {
    label: { en: 'How we work with you', ar: 'كيف نعمل معك' },
    items: [
      {
        title: { en: 'Tell us how you run things', ar: 'أخبرنا كيف تدير عملك' },
        body: {
          en: 'A walkthrough of your schedule, pricing, memberships and the tools you use today.',
          ar: 'جولة على جدولك وأسعارك وعضوياتك والأدوات التي تستخدمها اليوم.',
        },
      },
      {
        title: { en: 'We set it up and move your data', ar: 'نهيّئ المنصة وننقل بياناتك' },
        body: {
          en: 'Customers, package balances and memberships import from spreadsheets, so nobody starts from zero.',
          ar: 'نستورد العملاء وأرصدة الباقات والعضويات من جداول البيانات، فلا يبدأ أحد من الصفر.',
        },
      },
      {
        title: { en: 'Launch under your brand', ar: 'انطلق باسم علامتك' },
        body: {
          en: 'Your booking site, app and WhatsApp number go live with your name on them.',
          ar: 'موقع الحجز والتطبيق ورقم واتساب ينطلقون باسمك.',
        },
      },
      {
        title: { en: 'Keep asking', ar: 'واصل الطلب' },
        body: {
          en: 'Send requests any time. We review them with you and ship improvements every week.',
          ar: 'أرسل طلباتك في أي وقت، نراجعها معك ونطلق التحسينات كل أسبوع.',
        },
      },
    ],
  },
  quote: {
    en: 'Nothing was ever “not possible.” Every time we came to them with an idea, they found a way to make it happen, and usually made it better than we imagined.',
    ar: 'لم يكن هناك شيء «مستحيل». في كل مرة جئناهم بفكرة، وجدوا طريقة لتنفيذها، وغالبًا جاءت أفضل مما تخيلنا.',
  },
  quoteAttribution: {
    en: 'Frame Studio',
    ar: 'Frame Studio',
  },
} as const;

export const FINAL_CTA = {
  heading: {
    en: 'See Flow set up for a business like yours',
    ar: 'شاهد Flow مهيأة لمنشأة مثل منشأتك',
  },
  description: {
    en: 'A 30-minute walkthrough with the team that builds it, in Arabic or English.',
    ar: 'جولة مدتها 30 دقيقة مع الفريق الذي يبنيها، بالعربية أو الإنجليزية.',
  },
  paths: [
    {
      title: { en: 'Opening a new venue?', ar: 'تفتتح منشأة جديدة؟' },
      body: {
        en: 'Launch with booking, payments and your own app from day one.',
        ar: 'انطلق بالحجز والمدفوعات وتطبيقك الخاص من اليوم الأول.',
      },
    },
    {
      title: { en: 'Switching from another system?', ar: 'تنتقل من نظام آخر؟' },
      body: {
        en: 'We move your customers, balances and memberships for you.',
        ar: 'ننقل عملاءك وأرصدتهم وعضوياتهم نيابة عنك.',
      },
    },
  ],
  button: { en: 'Schedule a demo', ar: 'احجز عرضًا توضيحيًا' },
} as const;
